import moment from 'moment-timezone';
import { maskMoney, unmaskMoney } from '../../../../services/masks';
import { FieldDiff, InvoiceFormState, AdminEditInvoiceModalData } from './types';

const moneyToForm = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return maskMoney(String(Number(value).toFixed(2)), true);
};

const parseMoney = (value: string): number | null => {
  if (!value || !String(value).trim()) {
    return null;
  }
  const parsed = unmaskMoney(String(value));
  return Number.isFinite(parsed) ? parsed : null;
};

const toDate = (value?: string | Date | null): Date | null => {
  if (!value) {
    return null;
  }
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

const formatDateDisplay = (value: Date | null): string => {
  if (!value) {
    return '—';
  }
  return moment(value).format('DD/MM/YYYY');
};

const formatMoneyDisplay = (value: string): string => {
  if (!value || !String(value).trim()) {
    return '—';
  }
  return `R$ ${value}`;
};

const datesEqual = (a: Date | null, b: Date | null): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return moment(a).format('YYYY-MM-DD') === moment(b).format('YYYY-MM-DD');
};

export const mapInvoiceToFormState = (
  data: AdminEditInvoiceModalData
): InvoiceFormState => ({
  invoiceId: data.id,
  amount: moneyToForm(data.amount),
  dueDate: toDate(data.dueDate),
  paymentDate: toDate(data.paymentDate),
  status: data.status || '',
  estateName: data.estate?.name || '',
  insurerName: data.insurer?.name || '',
  contractCode: data.contractCode || '',
  emissionCode: data.emissionCode || '',
  contributionNumber: data.contributionNumber || ''
});

export const computeFieldDiffs = (
  original: InvoiceFormState,
  current: InvoiceFormState,
  statusLabel: (status: string) => string
): FieldDiff[] => {
  const diffs: FieldDiff[] = [];

  if (original.amount !== current.amount) {
    diffs.push({
      key: 'amount',
      label: 'Valor total',
      oldValue: formatMoneyDisplay(original.amount),
      newValue: formatMoneyDisplay(current.amount)
    });
  }

  if (!datesEqual(original.dueDate, current.dueDate)) {
    diffs.push({
      key: 'dueDate',
      label: 'Data de vencimento',
      oldValue: formatDateDisplay(original.dueDate),
      newValue: formatDateDisplay(current.dueDate)
    });
  }

  if (!datesEqual(original.paymentDate, current.paymentDate)) {
    diffs.push({
      key: 'paymentDate',
      label: 'Data de pagamento',
      oldValue: formatDateDisplay(original.paymentDate),
      newValue: formatDateDisplay(current.paymentDate)
    });
  }

  if (original.status !== current.status) {
    diffs.push({
      key: 'status',
      label: 'Status',
      oldValue: statusLabel(original.status) || '—',
      newValue: statusLabel(current.status) || '—'
    });
  }

  return diffs;
};

export const summarizeDiffs = (diffs: FieldDiff[]): string =>
  diffs.map(diff => diff.label).join(', ');

export const validateForm = (form: InvoiceFormState): string | null => {
  const amount = parseMoney(form.amount);
  if (amount === null || amount <= 0) {
    return 'Informe um valor total válido maior que zero.';
  }

  if (!form.dueDate) {
    return 'A data de vencimento é obrigatória.';
  }

  if (!form.status) {
    return 'O status é obrigatório.';
  }

  if (form.status === 'CLOSED' && !form.paymentDate) {
    return 'A data de pagamento é obrigatória quando o status é Pago.';
  }

  return null;
};

export const buildUpdatePayload = (
  form: InvoiceFormState,
  original: InvoiceFormState
) => {
  const payload: {
    amount?: number;
    dueDate?: string;
    paymentDate?: string | null;
    status?: string;
  } = {};

  if (form.amount !== original.amount) {
    const amount = parseMoney(form.amount);
    if (amount !== null) {
      payload.amount = amount;
    }
  }

  if (!datesEqual(form.dueDate, original.dueDate) && form.dueDate) {
    payload.dueDate = moment(form.dueDate).format('YYYY-MM-DD');
  }

  if (form.status !== original.status) {
    payload.status = form.status;
  }

  if (!datesEqual(form.paymentDate, original.paymentDate)) {
    payload.paymentDate = form.paymentDate
      ? moment(form.paymentDate).format('YYYY-MM-DD')
      : null;
  } else if (
    form.status !== original.status &&
    form.status !== 'CLOSED' &&
    original.paymentDate
  ) {
    payload.paymentDate = null;
  }

  return payload;
};
